import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { AGEING_BUCKETS } from '@wodi/domain';
import type { UnclaimedReportView } from '@wodi/shared';
import { Button, PageHeader, PrintTable } from '@wodi/ui';
import { useState } from 'react';

import { TableScroll } from '../layout/screen.tsx';
import { fetchUnclaimed, reportKeys } from '../api/reports.ts';
import { sessionQuery } from '../api/session.ts';
import { money } from '../billing/format.ts';
import { AGEING_LABELS } from '../reports/labels.ts';
import {
  AsAt,
  BackToReports,
  facilityWords,
  FacilityFilter,
  periodWords,
  PrintableReport,
  ReportActions,
  ReportStates,
  useFacilityChoice,
  useSheet,
} from '../reports/report-frame.tsx';
import styles from '../reports/reports.module.css';

type Facility = UnclaimedReportView['facilities'][number];

/**
 * The claims pipeline (plan §2.7, P14): what SHA and insurers owe on priced charges that
 * aren't on a claim yet, by payer and age, then SHA claims by their state. A charge on a
 * claim is followed by the claim, so no amount appears in both tables.
 */
export function UnclaimedReportPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const choice = useFacilityChoice('payer-share-unclaimed');
  const [facility, setFacility] = useState(session?.facility.id ?? '');
  const [shown, setShown] = useState({ facilityIds: choice.idsFor(facility) });
  const report = useQuery({
    queryKey: reportKeys.report('payer-share-unclaimed', shown),
    queryFn: () => fetchUnclaimed(shown),
  });
  const sheet = useSheet();
  const data = report.data;
  return (
    <div className={styles.page}>
      <BackToReports />
      <PageHeader title="Claims pipeline" />
      <p className={styles.summary}>
        What SHA and insurers owe on charges that aren't on a claim yet, by how long ago the service
        was given; then SHA claims by where they are.
      </p>
      <form
        className={styles.filters}
        onSubmit={(e) => {
          e.preventDefault();
          setShown({ facilityIds: choice.idsFor(facility) });
        }}
      >
        <FacilityFilter reportKey="payer-share-unclaimed" value={facility} onChange={setFacility} />
        <div>
          <Button type="submit" variant="primary">
            Show report
          </Button>
        </div>
      </form>
      <ReportStates
        query={report}
        what="payer share"
        isEmpty={(d) => d.facilities.every((f) => f.rows.length === 0 && f.claims.length === 0)}
        emptyText="No payer owes anything on charges so far, and there are no claims."
      >
        {(d) => (
          <>
            <AsAt dataAsAt={d.dataAsAt} />
            {d.facilities.map((f) => (
              <section key={f.facilityId} className={styles.section} aria-label={f.name}>
                {d.facilities.length > 1 ? (
                  <h2 className={styles.sectionHeading}>{f.name}</h2>
                ) : null}
                <SubHeading nested={d.facilities.length > 1}>
                  Payer share not yet claimed
                </SubHeading>
                {f.rows.length === 0 ? (
                  <p className={styles.muted}>Every payer share is on a claim.</p>
                ) : (
                  <TableScroll>
                    <table className={styles.table} aria-label="By payer and age">
                      <UnclaimedRows facility={f} />
                    </table>
                  </TableScroll>
                )}
                <SubHeading nested={d.facilities.length > 1}>SHA claims by state</SubHeading>
                {f.claims.length === 0 ? (
                  <p className={styles.muted}>No claims yet.</p>
                ) : (
                  <TableScroll>
                    <table className={styles.table} aria-label="Claims by state">
                      <ClaimRows facility={f} />
                    </table>
                  </TableScroll>
                )}
              </section>
            ))}
          </>
        )}
      </ReportStates>
      {data === undefined ? null : (
        <>
          <ReportActions
            reportKey="payer-share-unclaimed"
            sheet={sheet}
            printBody={{
              period: { from: data.asAt, to: data.asAt },
              facilityIds: data.facilities.map((f) => f.facilityId),
              rowCount: data.facilities.reduce((n, f) => n + f.rows.length + f.claims.length, 0),
            }}
            exportBody={shown}
            disabled={report.isFetching}
          />
          <PrintableReport
            sheet={sheet}
            title="Claims pipeline"
            facility={facilityWords(data.facilities.map((f) => f.name))}
            period={`As at ${periodWords({ from: data.asAt, to: data.asAt })}`}
            dataAsAt={data.dataAsAt}
          >
            {data.facilities.map((f) => (
              <div key={f.facilityId}>
                <PrintTable>
                  <UnclaimedRows facility={f} />
                </PrintTable>
                <PrintTable>
                  <ClaimRows facility={f} />
                </PrintTable>
              </div>
            ))}
          </PrintableReport>
        </>
      )}
    </div>
  );
}

function UnclaimedRows({ facility: f }: { facility: Facility }) {
  return (
    <>
      <thead>
        <tr>
          <th scope="col">Payer</th>
          {AGEING_BUCKETS.map((b) => (
            <th key={b} scope="col" data-numeric>
              {AGEING_LABELS[b]}
            </th>
          ))}
          <th scope="col" data-numeric>
            Total
          </th>
        </tr>
      </thead>
      <tbody>
        {f.rows.map((r) => (
          <tr key={r.payerId}>
            <td>
              {r.payerName}
              <span className={styles.secondLine}>{`${String(r.chargeCount)} charges`}</span>
            </td>
            {AGEING_BUCKETS.map((b) => (
              <td key={b} data-numeric>
                {money(r.byBucket[b])}
              </td>
            ))}
            <td data-numeric>{money(r.total)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          {AGEING_BUCKETS.map((b) => (
            <td key={b} />
          ))}
          <td data-numeric>{money(f.total)}</td>
        </tr>
      </tfoot>
    </>
  );
}

/** A heading under the page title, one level lower when facilities have their own. */
function SubHeading({ nested, children }: { nested: boolean; children: string }) {
  return nested ? (
    <h3 className={styles.sectionHeading}>{children}</h3>
  ) : (
    <h2 className={styles.sectionHeading}>{children}</h2>
  );
}

function ClaimRows({ facility: f }: { facility: Facility }) {
  return (
    <>
      <thead>
        <tr>
          <th scope="col">State</th>
          <th scope="col" data-numeric>
            Claims
          </th>
          <th scope="col" data-numeric>
            Claimed
          </th>
          <th scope="col" data-numeric>
            Approved
          </th>
          <th scope="col" data-numeric>
            Paid
          </th>
        </tr>
      </thead>
      <tbody>
        {f.claims.map((c) => (
          <tr key={c.state}>
            <td>{c.label}</td>
            <td data-numeric>{String(c.claimCount)}</td>
            <td data-numeric>{money(c.claimed)}</td>
            <td data-numeric>{money(c.approved)}</td>
            <td data-numeric>{money(c.paid)}</td>
          </tr>
        ))}
      </tbody>
    </>
  );
}
